/**
 * How much of the form still needs attention, read off react-hook-form's
 * errors.
 *
 * The status line states a number, so the number is worked out here and the bar
 * only renders it.
 */

import { type FieldErrors } from "react-hook-form";

/** Parts of an error node that describe it rather than name another problem. */
const DESCRIPTIVE_KEYS = new Set(["ref", "type", "types"]);

function countNode(node: unknown): number {
  if (node === null || typeof node !== "object") {
    return 0;
  }
  if (typeof (node as { message?: unknown }).message === "string") {
    return 1;
  }
  return Object.entries(node as Record<string, unknown>).reduce(
    (total, [key, child]) =>
      DESCRIPTIVE_KEYS.has(key) ? total : total + countNode(child),
    0,
  );
}

/**
 * How many fields are reported as wrong.
 *
 * `root` is left out: it carries the submission's own failure, which the form
 * shows as a banner rather than as something to go and fix.
 */
export function attentionCount(errors: FieldErrors): number {
  return Object.entries(errors)
    .filter(([key]) => key !== "root")
    .reduce((total, [, value]) => total + countNode(value), 0);
}

/** Where the form stands, as a tag the bar turns into words. */
export type FormStatusKind = "unsubmitted" | "clean" | "attention";

/**
 * What the status line has to say.
 *
 * Before the first submit nothing has been checked, so the line points at what
 * is left to do rather than claiming the form is sound.
 */
export function formStatusKind(
  submitted: boolean,
  needingAttention: number,
): FormStatusKind {
  if (!submitted) {
    return "unsubmitted";
  }
  return needingAttention === 0 ? "clean" : "attention";
}
